import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { closePingTask, markTaskAsCompleted } from "@/api/tasks";
import { Divider } from "@/components/misc";
import { TaskProps } from "@/interfaces/tasks_types";
import { NotesSection } from "../ProjectProgress/Utils/NotesSection";
import { CardDropdownTask } from "./Utils/CardDropdown";
import { CardBase, PingedCardAlert } from "./Utils/CardHelper";
import { CategoryIcon } from "./Utils/CategoryIcon";
import { ProgressBar } from "./Utils/ProgressBar";

type TaskCardProps = {
	task: TaskProps;
	isNew: boolean;
	onView: () => void;
};
/**
 * TaskCard Component
 *
 * @param task - The task to be displayed
 * @param isNew - Whether the task is new
 * @param onView - This will turn isNew to false when the task is viewed (or seen)
 * @returns A card displaying the task's details
 *
 */
export function TaskCard({ task, isNew, onView }: TaskCardProps) {
	// This is used because onView would make isNew false immediately.
	// So we need to use a local state to prevent unnecessary re-renders
	const [isNewLocal, setIsNewLocal] = useState(isNew);
	const [progressColor, setProgressColor] = useState("bg-white");
	const navigate = useNavigate();
	const location = useLocation();

	// This is used to encode the task_id so that it can be used in the URL
	// So cases where the task id has slashes or other special characters can be handled
	const encodedID = encodeURIComponent(task.task_id ?? "");

	const handleMark = async () => {
		if (confirm("Are you sure you want to mark this task as completed?")) {
			if (task.task_id) {
				const response = await markTaskAsCompleted(task.task_id);
				if (response.operation === "COMPLETED") {
					navigate(location.pathname, { replace: true });
				}
			} else {
				alert("Task ID is not defined");
			}
		}
	};

	const handlePing = async () => {
		if (!task.pinged) {
			return;
		}

		const response = await closePingTask(task.task_id ?? "");
		if (response.operation === "COMPLETED") {
			navigate(location.pathname, { replace: true });
		}
	};

	useEffect(() => {
		if (isNewLocal) {
			setIsNewLocal(false);
			onView();
		}

		if (task.completion_percentage > 75) {
			setProgressColor("bg-green-500");
		} else if (task.completion_percentage >= 50) {
			setProgressColor("bg-yellow-200");
		} else {
			setProgressColor("bg-red-500");
		}
	}, [isNewLocal, onView, task.completion_percentage]);

	return (
		<CardBase
			isNew={isNewLocal}
			hoverChildren={
				<CardDropdownTask
					edit_task_route={`/tasks/update_task/${encodedID}`}
					mark_as_completed_fn={handleMark}
					mark_as_pinged_fn={handlePing}
					folder_path={task.folder_location ?? ""}
				/>
			}
		>
			{task.pinged && (
				<PingedCardAlert text="Please complete this task soon." />
			)}

			<div className="flex flex-row items-center">
				<CategoryIcon category={task.category} />
				<h5 className="font-bold">{task.title}</h5>
				{task.priority == "Urgent" && (
					<p className="font-bold text-right flex float-right pl-4 text-red-800 ">
						( Urgent )
					</p>
				)}
			</div>

			<Divider />

			<NotesSection notes={task.description ?? ""} />

			<div className="flex flex-row justify-between">
				<p className="text-sm text-red-800 dark:text-red-400">
					Due: {task.due_date}
				</p>
				<p className="text-sm text-slate-800 dark:text-slate-100">
					Created by: {task.assigned_by?.split("|")[0] ?? "Unassigned"}
				</p>
			</div>

			<ProgressBar
				progressColor={progressColor}
				completionPercentage={task.completion_percentage}
			/>
		</CardBase>
	);
}
